// ============================================================
// SAC GRID VIEW - Composant reutilisable base OrdersGridPage
// ------------------------------------------------------------
// Rend une grille tableau dense pour les pages Service Client
// et Repartition. Chaque page passe ses propres props:
//   - workflowIn        : ['nouvelle'] | ['rdv_planifie'] | ['attribuee_repartiteur']
//   - title, subtitle, iconColor
//   - assignmentType    : 'logistique' | 'dispatcher' | null
//   - showTransitionBtn : bouton "Envoyer vers ..." (transition workflow)
//   - nextWorkflow      : statut cible du bouton transition
//   - nextWorkflowLabel : libelle du bouton transition
//   - scopeMine         : bool -> ajoute ?scope=mine (isolation par agent)
//   - subTabs           : [{ href, label, active }]  onglets en-tete
// ============================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAdminApi } from '../hooks/useAdminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, FileSpreadsheet, Plus, MoreVertical, X, User, AlertCircle,
  Loader2, Edit, UserPlus, Trash2, ChevronLeft, ChevronRight, ArrowRightCircle, RefreshCw,
  Volume2, VolumeX, Route,
} from 'lucide-react';
import { toast } from 'sonner';
import { DispatchOrderModal } from './dispatch/DispatchModals';
import { playDispatchAlarm, extractReturnedStopIds } from '../../utils/dispatchAlarm';

// ------------------ COLONNES ------------------
const ALL_COLUMNS = [
  { key: 'order_number', label: 'N° Commande', width: 'w-28', mandatory: true },
  { key: 'status', label: 'Statut', width: 'w-32', mandatory: true },
  { key: 'client', label: 'Client', width: 'w-44' },
  { key: 'logistique', label: 'Agent logistique', width: 'w-40' },
  { key: 'dispatcher', label: 'Répartiteur', width: 'w-40' },
  { key: 'driver', label: 'Chauffeur Assigné', width: 'w-44' },
  { key: 'pickup_date', label: 'Date Cueill.', width: 'w-28' },
  { key: 'pickup_time', label: 'Heure Cueill.', width: 'w-24' },
  { key: 'pickup_city', label: 'Ville Cueill.', width: 'w-36' },
  { key: 'delivery_date', label: 'Date Livr.', width: 'w-28' },
  { key: 'delivery_time', label: 'Heure Livr.', width: 'w-24' },
  { key: 'delivery_city', label: 'Ville Livr.', width: 'w-36' },
  { key: 'pallets', label: 'Pal.', width: 'w-16 text-right' },
];

const STATUS_META = {
  pending:     { label: 'En attente',      class: 'bg-slate-600/30 text-slate-300 border-slate-500/40' },
  nouvelle:    { label: 'À planifier',     class: 'bg-slate-600/30 text-slate-300 border-slate-500/40' },
  rdv_planifie:{ label: 'RDV planifié',    class: 'bg-blue-500/25 text-blue-200 border-blue-500/50' },
  attribuee_repartiteur: { label: 'Attribuée', class: 'bg-emerald-500/25 text-emerald-200 border-emerald-500/50' },
  envoye:      { label: 'Envoyé',          class: 'bg-yellow-500/25 text-yellow-200 border-yellow-500/50' },
  accepte:     { label: 'Accepté',         class: 'bg-green-500/25 text-green-200 border-green-500/50' },
  en_cours:    { label: 'En cours',        class: 'bg-green-500/25 text-green-200 border-green-500/50' },
  complete:    { label: 'Complété',        class: 'bg-slate-100 text-slate-900 border-slate-200' },
  retourne:    { label: 'Retour SAC',      class: 'bg-red-500/30 text-red-200 border-red-500/60' },
  blocked:     { label: 'Groupé',          class: 'bg-slate-600/30 text-slate-300 border-slate-500/40' },
  frozen:      { label: 'Bloqué',          class: 'bg-yellow-900/50 text-yellow-200 border-yellow-800/70' },
  split:       { label: 'Divisée',         class: 'bg-purple-500/20 text-purple-300 border-purple-500/40' },
  cancelled:   { label: 'Annulé',          class: 'bg-red-500/10 text-red-400 border-red-500/30' },
  draft:       { label: 'Brouillon',       class: 'bg-slate-700/50 text-slate-300 border-slate-600/50' },
};

const STATUS_ALIAS = { assigned: 'envoye', in_progress: 'en_cours', completed: 'complete' };
const normalizeStatus = (s) => STATUS_ALIAS[s] || s;

const todayIso = () => new Date().toISOString().slice(0, 10);

function deriveRow(order) {
  const stops = order.stops || [];
  const isPickup = (s) => s.stop_type === 'cueillette' || s.stop_type === 'pickup';
  const isDelivery = (s) => s.stop_type === 'livraison' || s.stop_type === 'delivery';
  const pickup = stops.find(isPickup) || stops[0] || {};
  const delivery = [...stops].reverse().find(isDelivery) || stops[stops.length - 1] || {};
  const driverIds = [...new Set(stops.map((s) => s.assigned_driver_id).filter(Boolean))];
  const totalPallets = stops.reduce((sum, s) => sum + (s.items?.length || 0), 0);
  const wfStatus = order.workflow_status;
  const rawStatus = normalizeStatus(pickup.block_status || delivery.block_status || order.block_status || pickup.status || 'pending');
  const hasReturned = stops.some((s) => s.status === 'retourne' || s.block_status === 'retourne');
  return {
    id: order.id,
    order_number: order.order_number || order.id.slice(0, 8),
    workflow_status: wfStatus,
    hasReturned,
    status: hasReturned ? 'retourne' : (wfStatus === 'nouvelle' ? 'nouvelle' : wfStatus === 'rdv_planifie' ? 'rdv_planifie' : rawStatus),
    client: pickup.client_name || delivery.client_name || '—',
    driverIds,
    assigned_logistique_id: order.assigned_logistique_id || null,
    assigned_logistique_name: order.assigned_logistique_name || null,
    assigned_dispatcher_id: order.assigned_dispatcher_id || null,
    assigned_dispatcher_name: order.assigned_dispatcher_name || null,
    pickup_date: pickup.required_date || '',
    pickup_time: pickup.required_time || '',
    pickup_city: pickup.city || '',
    delivery_date: delivery.required_date || '',
    delivery_time: delivery.required_time || '',
    delivery_city: delivery.city || '',
    pallets: totalPallets || '',
    _raw: order,
  };
}

// ============================================================
// MAIN COMPONENT
// ============================================================
const SacGridView = ({
  title,
  subtitle,
  iconLetters = 'SC',
  iconColor = 'blue',
  workflowIn = [],
  assignmentType = null,
  allowShortcuts = false,
  showReturnsAlarm = false,      // active le bouton d'alarme sonore retours SAC + fond rouge
  nextWorkflow = null,
  nextWorkflowLabel = null,
  scopeMine = false,
  subTabs = [],
  defaultVisibleColumns = null,
  storageKey = 'sacGrid',
}) => {
  const { get, post, del } = useAdminApi();
  const { token, user } = useAdminAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [dispatchers, setDispatchers] = useState([]);
  const [alarmMuted, setAlarmMuted] = useState(() => localStorage.getItem('dispatchAlarmMuted') === '1');
  const previousReturnedIdsRef = React.useRef(new Set());
  const [returnedCount, setReturnedCount] = useState(0);
  useEffect(() => { localStorage.setItem('dispatchAlarmMuted', alarmMuted ? '1' : '0'); }, [alarmMuted]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    agentIds: new Set(),
    dateFrom: '',
    dateTo: '',
  });

  const defaultCols = defaultVisibleColumns || ALL_COLUMNS.filter((c) => {
    if (c.key === 'logistique' && assignmentType !== 'logistique' && !workflowIn.includes('attribuee_repartiteur')) return false;
    if (c.key === 'dispatcher' && assignmentType !== 'dispatcher' && !workflowIn.includes('attribuee_repartiteur')) return false;
    if (c.key === 'driver' && !workflowIn.includes('attribuee_repartiteur')) return false;
    return !c.hidden;
  }).map((c) => c.key);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}.visibleColumns`);
    if (saved) return new Set(JSON.parse(saved));
    return new Set(defaultCols);
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}.columnOrder`);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const validKeys = new Set(ALL_COLUMNS.map((c) => c.key));
        const filtered = parsed.filter((k) => validKeys.has(k));
        const missing = ALL_COLUMNS.map((c) => c.key).filter((k) => !filtered.includes(k));
        return [...filtered, ...missing];
      } catch { /* fallback */ }
    }
    return ALL_COLUMNS.map((c) => c.key);
  });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [contextMenu, setContextMenu] = useState(null);
  const [editingOrder, setEditingOrder] = useState(null);
  const [assignFor, setAssignFor] = useState(null); // { orderIds: [...], mode: 'primary' | 'shortcut-dispatcher' }
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [transitioning, setTransitioning] = useState(false);

  // ------------ Fetch ------------
  const fetchOrders = useCallback(async () => {
    const params = new URLSearchParams();
    if (workflowIn && workflowIn.length > 0) params.set('workflow_in', workflowIn.join(','));
    if (scopeMine) params.set('scope', 'mine');
    const res = await get(`/dispatch/orders?${params.toString()}`);
    if (res.success) {
      const newOrders = res.data.orders || [];
      setOrders(newOrders);
      if (showReturnsAlarm) {
        const currentIds = extractReturnedStopIds(newOrders);
        setReturnedCount(currentIds.size);
        const hasNew = [...currentIds].some(id => !previousReturnedIdsRef.current.has(id));
        if (hasNew && previousReturnedIdsRef.current.size > 0 && !alarmMuted) {
          playDispatchAlarm();
        }
        previousReturnedIdsRef.current = currentIds;
      }
    } else {
      toast.error('Erreur chargement commandes');
    }
    setLoading(false);
  }, [get, workflowIn, scopeMine, showReturnsAlarm, alarmMuted]);

  const fetchDrivers = useCallback(async () => {
    const res = await get('/saas/dashboard/drivers');
    if (res.success) setDrivers(res.data || []);
  }, [get]);

  const fetchAgents = useCallback(async () => {
    // Charger la liste principale selon assignmentType (colonne filtre)
    if (assignmentType === 'logistique') {
      const res = await get('/dispatch/logistique-agents');
      if (res.success) setAgents(res.data || []);
    } else if (assignmentType === 'dispatcher') {
      const res = await get('/dispatch/dispatchers');
      if (res.success) setAgents(res.data || []);
    }
    // Toujours charger les repartiteurs si raccourci actif OU si assignmentType n'est pas dispatcher
    if (allowShortcuts || assignmentType === 'logistique' || workflowIn.includes('attribuee_repartiteur')) {
      const resDisp = await get('/dispatch/dispatchers');
      if (resDisp.success) setDispatchers(resDisp.data || []);
    }
  }, [get, assignmentType, allowShortcuts, workflowIn]);

  useEffect(() => {
    setLoading(true);
    fetchOrders();
    fetchDrivers();
    fetchAgents();
  }, [fetchOrders, fetchDrivers, fetchAgents]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchOrders, 15000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchOrders]);

  useEffect(() => {
    localStorage.setItem(`${storageKey}.visibleColumns`, JSON.stringify([...visibleColumns]));
  }, [visibleColumns, storageKey]);
  useEffect(() => {
    localStorage.setItem(`${storageKey}.columnOrder`, JSON.stringify(columnOrder));
  }, [columnOrder, storageKey]);

  const moveColumn = useCallback((key, direction) => {
    setColumnOrder((prev) => {
      const visibleKeysInOrder = prev.filter((k) => visibleColumns.has(k));
      const visIdx = visibleKeysInOrder.indexOf(key);
      if (visIdx < 0) return prev;
      const targetVisIdx = visIdx + direction;
      if (targetVisIdx < 0 || targetVisIdx >= visibleKeysInOrder.length) return prev;
      const swapKey = visibleKeysInOrder[targetVisIdx];
      const next = [...prev];
      const iA = next.indexOf(key);
      const iB = next.indexOf(swapKey);
      [next[iA], next[iB]] = [next[iB], next[iA]];
      return next;
    });
  }, [visibleColumns]);

  const reorderColumn = useCallback((srcKey, dstKey) => {
    if (srcKey === dstKey) return;
    setColumnOrder((prev) => {
      const next = prev.filter((k) => k !== srcKey);
      const dstIdx = next.indexOf(dstKey);
      if (dstIdx < 0) return prev;
      next.splice(dstIdx, 0, srcKey);
      return next;
    });
  }, []);

  // ------------ Data ------------
  const rows = useMemo(() => orders.map(deriveRow), [orders]);

  const filteredRows = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.agentIds.size > 0) {
        const agentId = assignmentType === 'logistique' ? r.assigned_logistique_id
          : assignmentType === 'dispatcher' ? r.assigned_dispatcher_id
          : null;
        if (filters.agentIds.has('unassigned')) {
          if (agentId) return false;
        } else if (!agentId || !filters.agentIds.has(agentId)) return false;
      }
      if (filters.dateFrom && r.pickup_date < filters.dateFrom) return false;
      if (filters.dateTo && r.pickup_date > filters.dateTo) return false;
      if (q) {
        const hay = `${r.order_number} ${r.client} ${r.pickup_city} ${r.delivery_city} ${r.assigned_logistique_name || ''} ${r.assigned_dispatcher_name || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filters, assignmentType]);

  const stats = useMemo(() => ({
    count: filteredRows.length,
    totalPallets: filteredRows.reduce((s, r) => s + (Number(r.pallets) || 0), 0),
  }), [filteredRows]);

  // ------------ Actions ------------
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelectedIds((prev) =>
    prev.size === filteredRows.length ? new Set() : new Set(filteredRows.map((r) => r.id))
  );

  const openContextMenu = (row, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      orderId: row.id, row,
      x: Math.min(rect.left, window.innerWidth - 240),
      y: rect.bottom + 4,
    });
  };

  const handleAssign = async (orderIds, agentId, mode = 'primary') => {
    // mode 'primary' -> respecte assignmentType. mode 'shortcut-dispatcher' -> force assign-dispatcher
    const useDispatcher = mode === 'shortcut-dispatcher' || assignmentType === 'dispatcher';
    const endpoint = useDispatcher ? 'assign-dispatcher' : 'assign-logistique';
    const bodyKey = useDispatcher ? 'dispatcher_id' : 'logistique_id';
    let ok = 0, ko = 0;
    for (const id of orderIds) {
      const res = await post(`/dispatch/orders/${id}/${endpoint}`, { [bodyKey]: agentId });
      if (res.success) ok++; else ko++;
    }
    if (ok > 0) toast.success(`${ok} commande(s) attribuée(s)${mode === 'shortcut-dispatcher' ? ' au répartiteur (raccourci)' : ''}`);
    if (ko > 0) toast.error(`${ko} erreur(s)`);
    setAssignFor(null);
    setSelectedIds(new Set());
    fetchOrders();
  };

  const handleClearReturns = async (orderId, orderNumber) => {
    const res = await post(`/dispatch/orders/${orderId}/clear-returns`, {});
    if (res.success) {
      toast.success(`Retour de ${orderNumber} marqué comme traité`);
      fetchOrders();
    } else {
      toast.error(res.error || 'Erreur');
    }
  };

  const handleTransition = async () => {
    if (!nextWorkflow) return;
    setTransitioning(true);
    let ok = 0, ko = 0;
    for (const id of [...selectedIds]) {
      const res = await post(`/dispatch/orders/${id}/transition`, { to_status: nextWorkflow });
      if (res.success) ok++; else ko++;
    }
    setTransitioning(false);
    if (ok > 0) toast.success(`${ok} commande(s) transférée(s)`);
    if (ko > 0) toast.error(`${ko} erreur(s) - permissions ou statut invalide`);
    setSelectedIds(new Set());
    fetchOrders();
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    let ok = 0, ko = 0;
    for (const id of [...selectedIds]) {
      const res = await del(`/dispatch/orders/${id}`);
      if (res.success) ok++; else ko++;
    }
    setDeleting(false);
    setShowDeleteConfirm(false);
    setSelectedIds(new Set());
    if (ok > 0) toast.success(`${ok} commande(s) supprimée(s)`);
    if (ko > 0) toast.error(`${ko} erreur(s)`);
    fetchOrders();
  };

  const handleSendToOptimization = async () => {
    const ids = [...selectedIds];
    if (ids.length < 2) {
      toast.error('Sélectionnez au moins 2 commandes à regrouper');
      return;
    }
    const sourceType = workflowIn.includes('attribuee_repartiteur') ? 'dispatch' : 'sac';
    const res = await post('/dispatch/route-opt/create', { order_ids: ids, source_type: sourceType });
    if (res.success) {
      toast.success(`${ids.length} commandes envoyées à l'optimisation`);
      setSelectedIds(new Set());
      fetchOrders();
      navigate(`/admin/route-optimisation?group=${res.data.group_id}`);
    } else {
      toast.error(res.error || 'Erreur lors du regroupement');
    }
  };

  const driverNameById = useMemo(() => {
    const map = {};
    drivers.forEach((d) => { map[d.id || d._id] = `${d.first_name || ''} ${d.last_name || ''}`.trim() || d.email; });
    return map;
  }, [drivers]);

  const cols = columnOrder
    .filter((k) => visibleColumns.has(k))
    .map((k) => ALL_COLUMNS.find((c) => c.key === k))
    .filter(Boolean);

  return (
    <div className="h-full min-h-screen bg-slate-950 text-white flex flex-col font-sans" data-testid={`sac-grid-${storageKey}`} onClick={() => setContextMenu(null)}>
      <Header
        title={title}
        subtitle={subtitle}
        iconLetters={iconLetters}
        iconColor={iconColor}
        subTabs={subTabs}
        onNavigate={navigate}
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen((s) => !s)}
        searchValue={filters.search}
        onSearchChange={(v) => setFilters((f) => ({ ...f, search: v }))}
        autoRefresh={autoRefresh}
        onToggleAutoRefresh={() => setAutoRefresh((v) => !v)}
        onManualRefresh={fetchOrders}
        currentUser={user}
        showReturnsAlarm={showReturnsAlarm}
        alarmMuted={alarmMuted}
        onToggleAlarm={() => setAlarmMuted(m => !m)}
        returnedCount={returnedCount}
      />
      <div className="flex-1 flex overflow-hidden">
        {sidebarOpen && (
          <FiltersSidebar
            filters={filters}
            setFilters={setFilters}
            agents={agents}
            assignmentType={assignmentType}
            rows={rows}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
          />
        )}
        <main className="flex-1 flex flex-col min-w-0 bg-slate-950 overflow-hidden">
          {selectedIds.size > 0 && (
            <BatchToolbar
              count={selectedIds.size}
              assignmentType={assignmentType}
              allowShortcuts={allowShortcuts}
              onAssign={() => setAssignFor({ orderIds: [...selectedIds], mode: 'primary' })}
              onShortcutDispatcher={() => setAssignFor({ orderIds: [...selectedIds], mode: 'shortcut-dispatcher' })}
              onDelete={() => setShowDeleteConfirm(true)}
              onTransition={nextWorkflow ? handleTransition : null}
              onSendToOptimization={handleSendToOptimization}
              nextWorkflowLabel={nextWorkflowLabel}
              transitioning={transitioning}
              onClear={() => setSelectedIds(new Set())}
            />
          )}
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center h-64 text-slate-500">
                <Loader2 className="animate-spin mr-2" /> Chargement…
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500 gap-2">
                <FileSpreadsheet size={48} className="opacity-30" />
                <p className="text-sm">Aucune commande dans cette vue.</p>
              </div>
            ) : (
              <OrdersTable
                cols={cols}
                rows={filteredRows}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onRowClick={openContextMenu}
                onOpenRow={(row) => setEditingOrder(row._raw)}
                driverNameById={driverNameById}
                onMoveColumn={moveColumn}
                onReorderColumn={reorderColumn}
              />
            )}
          </div>
          <StatsFooter stats={stats} totalOrders={rows.length} />
        </main>
      </div>

      {contextMenu && (
        <ContextMenu
          menu={contextMenu}
          assignmentType={assignmentType}
          allowShortcuts={allowShortcuts}
          onClose={() => setContextMenu(null)}
          onEdit={() => { setEditingOrder(contextMenu.row._raw); setContextMenu(null); }}
          onAssign={() => { setAssignFor({ orderIds: [contextMenu.orderId], mode: 'primary' }); setContextMenu(null); }}
          onShortcutDispatcher={() => { setAssignFor({ orderIds: [contextMenu.orderId], mode: 'shortcut-dispatcher' }); setContextMenu(null); }}
          onDelete={() => { setSelectedIds(new Set([contextMenu.orderId])); setShowDeleteConfirm(true); setContextMenu(null); }}
          onClearReturns={() => { handleClearReturns(contextMenu.orderId, contextMenu.row.order_number); setContextMenu(null); }}
          hasReturned={contextMenu.row.hasReturned}
        />
      )}

      {assignFor && (
        <AssignAgentModal
          orderIds={assignFor.orderIds}
          agents={assignFor.mode === 'shortcut-dispatcher' ? dispatchers : agents}
          assignmentType={assignFor.mode === 'shortcut-dispatcher' ? 'dispatcher' : assignmentType}
          isShortcut={assignFor.mode === 'shortcut-dispatcher'}
          onClose={() => setAssignFor(null)}
          onPick={(agentId) => handleAssign(assignFor.orderIds, agentId, assignFor.mode)}
        />
      )}

      {editingOrder && (
        <DispatchOrderModal
          orderId={editingOrder.id}
          onClose={() => setEditingOrder(null)}
          onSave={() => { fetchOrders(); setEditingOrder(null); }}
          token={token}
          selectedDate={todayIso()}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal
          count={selectedIds.size}
          orderNumbers={filteredRows.filter((r) => selectedIds.has(r.id)).map((r) => r.order_number)}
          deleting={deleting}
          onCancel={() => !deleting && setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
        />
      )}
    </div>
  );
};

// ============================================================
// HEADER
// ============================================================
const iconColorMap = {
  blue:  'bg-blue-500/10 border-blue-500/30 text-blue-400',
  cyan:  'bg-cyan-500/10 border-cyan-500/30 text-cyan-400',
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  amber: 'bg-amber-500/10 border-amber-500/30 text-amber-400',
};

const Header = ({ title, subtitle, iconLetters, iconColor, subTabs, onNavigate, sidebarOpen, onToggleSidebar, searchValue, onSearchChange, autoRefresh, onToggleAutoRefresh, onManualRefresh, currentUser, showReturnsAlarm, alarmMuted, onToggleAlarm, returnedCount }) => (
  <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex flex-wrap items-center justify-between gap-3 sticky top-0 z-30">
    <div className="flex items-center gap-4 flex-wrap">
      <div className="flex items-center gap-2">
        <div className={`w-9 h-9 rounded-lg border flex items-center justify-center font-bold ${iconColorMap[iconColor] || iconColorMap.blue}`}>
          {iconLetters}
        </div>
        <div>
          <h1 className="text-lg font-bold uppercase tracking-wide text-white leading-tight">{title}</h1>
          <p className="text-xs text-slate-400">{subtitle}</p>
        </div>
      </div>
      {subTabs && subTabs.length > 0 && (
        <div className="flex items-center bg-slate-950 border border-slate-800 p-1 rounded-lg ml-2 flex-wrap">
          {subTabs.map((tab) => (
            <button
              key={tab.href}
              onClick={() => onNavigate(tab.href)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                tab.active
                  ? 'bg-amber-500 text-black shadow-sm'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
              data-testid={`subtab-${tab.href}`}
            >
              {tab.icon && <tab.icon size={13} />}
              {tab.label}
            </button>
          ))}
        </div>
      )}
    </div>
    <div className="flex items-center gap-2.5 flex-wrap">
      {currentUser && (
        <div className="text-[10px] text-slate-400 mr-2 hidden md:block">
          Connecté&nbsp;: <span className="text-white font-semibold">{currentUser.first_name}</span>
          <span className="text-slate-500 ml-1">({currentUser.role?.replace('_', ' ')})</span>
        </div>
      )}
      <div className="relative w-64 md:w-72">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Recherche PO, client, ville..."
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 text-white pl-9 pr-3 py-1.5 rounded-lg text-xs placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          data-testid="orders-search-input"
        />
      </div>
      <button
        onClick={onManualRefresh}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 min-h-11"
        data-testid="manual-refresh-btn"
      >
        <span className={`w-2 h-2 rounded-full ${autoRefresh ? 'bg-emerald-400 animate-pulse' : 'bg-slate-500'}`} />
        <span>{autoRefresh ? 'Live' : 'Pause'}</span>
      </button>
      {showReturnsAlarm && (
        <button
          onClick={onToggleAlarm}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border min-h-11 relative ${
            alarmMuted
              ? 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'
              : returnedCount > 0
                ? 'bg-red-500/20 border-red-500/50 text-red-300 animate-pulse'
                : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300'
          }`}
          data-testid="toggle-dispatch-alarm-header-btn"
          title={alarmMuted ? "Activer l'alarme sonore des retours SAC" : "Couper l'alarme sonore"}
        >
          {alarmMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
          <span className="hidden sm:inline">Retours: {alarmMuted ? 'OFF' : 'ON'}</span>
          {returnedCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {returnedCount}
            </span>
          )}
        </button>
      )}
      <button
        onClick={onToggleAutoRefresh}
        className={`px-2 py-2 rounded-lg text-xs font-medium border min-h-11 ${autoRefresh ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'}`}
        data-testid="toggle-auto-refresh"
      >
        {autoRefresh ? '⏸' : '▶'}
      </button>
      <button
        onClick={onToggleSidebar}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors min-h-11 ${
          sidebarOpen ? 'bg-slate-800 border-amber-500/40 text-amber-400' : 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800'
        }`}
        data-testid="toggle-filters-btn"
      >
        <Filter size={14} /><span>Filtres</span>
      </button>
    </div>
  </div>
);

// ============================================================
// FILTERS SIDEBAR
// ============================================================
const FiltersSidebar = ({ filters, setFilters, agents, assignmentType, rows, visibleColumns, setVisibleColumns }) => {
  const toggleAgent = (id) => setFilters((f) => {
    const next = new Set(f.agentIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { ...f, agentIds: next };
  });
  const toggleColumn = (key) => setVisibleColumns((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const unassignedCount = useMemo(() => {
    if (!assignmentType) return 0;
    return rows.filter((r) => {
      const id = assignmentType === 'logistique' ? r.assigned_logistique_id : r.assigned_dispatcher_id;
      return !id;
    }).length;
  }, [rows, assignmentType]);

  return (
    <aside className="w-64 bg-slate-900/95 border-r border-slate-800 flex flex-col shrink-0 overflow-y-auto text-xs p-3 space-y-4">
      <div>
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Raccourcis</span>
        <div className="grid grid-cols-2 gap-1.5 mt-1.5">
          <button
            onClick={() => setFilters((f) => ({ ...f, dateFrom: todayIso(), dateTo: todayIso() }))}
            className="px-2 py-1.5 rounded bg-slate-800/80 text-slate-300 border border-slate-700/60 text-left hover:bg-slate-700"
            data-testid="filter-today"
          >
            Aujourd'hui
          </button>
          <button
            onClick={() => setFilters((f) => ({ ...f, dateFrom: '', dateTo: '' }))}
            className="px-2 py-1.5 rounded bg-slate-800/80 text-slate-300 border border-slate-700/60 text-left hover:bg-slate-700"
            data-testid="filter-all-dates"
          >
            Toutes dates
          </button>
        </div>
      </div>

      {assignmentType && (
        <div className="space-y-1.5">
          <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">
            {assignmentType === 'logistique' ? 'Agent logistique' : 'Répartiteur'}
          </span>
          <div className="space-y-1 bg-slate-950 p-2 rounded-lg border border-slate-800 max-h-40 overflow-y-auto">
            <label className="flex items-center justify-between px-1.5 py-0.5 rounded hover:bg-slate-900 cursor-pointer text-amber-400 font-semibold">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filters.agentIds.has('unassigned')}
                  onChange={() => toggleAgent('unassigned')}
                  className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900"
                />
                <span>Non attribués</span>
              </div>
              <span className="bg-amber-500/20 px-1 rounded text-[10px]">{unassignedCount}</span>
            </label>
            {agents.map((a) => (
              <label key={a.id} className="flex items-center gap-2 px-1.5 py-0.5 rounded hover:bg-slate-900 cursor-pointer text-slate-300">
                <input
                  type="checkbox"
                  checked={filters.agentIds.has(a.id)}
                  onChange={() => toggleAgent(a.id)}
                  className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900"
                />
                <span className="truncate text-xs">{`${a.first_name || ''} ${a.last_name || ''}`.trim()}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Date Cueillette</span>
        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
          className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-2 py-1.5 rounded text-xs"
          data-testid="filter-date-from"
        />
        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
          className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-2 py-1.5 rounded text-xs"
          data-testid="filter-date-to"
        />
      </div>

      <div className="space-y-1.5">
        <span className="text-[10px] font-bold tracking-wider text-slate-400 uppercase">Colonnes visibles</span>
        <div className="space-y-1 bg-slate-950 p-2 rounded-lg border border-slate-800">
          {ALL_COLUMNS.map((c) => (
            <label key={c.key} className={`flex items-center gap-2 px-1 py-0.5 rounded cursor-pointer ${c.mandatory ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-900'} text-slate-300`}>
              <input
                type="checkbox"
                checked={visibleColumns.has(c.key)}
                onChange={() => !c.mandatory && toggleColumn(c.key)}
                disabled={c.mandatory}
                className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900"
              />
              <span className="text-xs">{c.label}{c.mandatory && ' *'}</span>
            </label>
          ))}
        </div>
      </div>
    </aside>
  );
};

// ============================================================
// BATCH TOOLBAR
// ============================================================
const BatchToolbar = ({ count, assignmentType, allowShortcuts, onAssign, onShortcutDispatcher, onDelete, onTransition, onSendToOptimization, nextWorkflowLabel, transitioning, onClear }) => (
  <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between text-xs flex-wrap gap-2">
    <div className="flex items-center gap-3 flex-wrap">
      <span className="font-bold text-amber-400">{count} sélectionnée{count > 1 ? 's' : ''}</span>
      <span className="text-slate-500">|</span>
      {onSendToOptimization && (
        <button
          onClick={onSendToOptimization}
          className="px-3 py-1.5 rounded bg-purple-500 hover:bg-purple-400 text-white font-bold min-h-9 flex items-center gap-1.5"
          data-testid="batch-send-optimization-btn"
          title="Regrouper les commandes sélectionnées et optimiser la route"
        >
          <Route size={13} /> Envoyer à l'optimisation
        </button>
      )}
      {assignmentType && (
        <button
          onClick={onAssign}
          className="px-3 py-1.5 rounded bg-blue-500 hover:bg-blue-400 text-white font-bold min-h-9 flex items-center gap-1.5"
          data-testid="batch-assign-btn"
        >
          <UserPlus size={13} />
          {assignmentType === 'logistique' ? 'Attribuer à un agent…' : 'Attribuer au répartiteur…'}
        </button>
      )}
      {allowShortcuts && assignmentType !== 'dispatcher' && (
        <button
          onClick={onShortcutDispatcher}
          className="px-3 py-1.5 rounded bg-emerald-500/20 hover:bg-emerald-500/40 border border-emerald-500/40 text-emerald-300 hover:text-emerald-100 font-bold min-h-9 flex items-center gap-1.5"
          data-testid="batch-shortcut-dispatcher-btn"
          title="Sauter l'étape logistique et attribuer directement au répartiteur"
        >
          <ArrowRightCircle size={13} /> Raccourci → répartiteur…
        </button>
      )}
      {onTransition && (
        <button
          onClick={onTransition}
          disabled={transitioning}
          className="px-3 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-black font-bold min-h-9 flex items-center gap-1.5 disabled:opacity-60"
          data-testid="batch-transition-btn"
        >
          {transitioning ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightCircle size={13} />}
          {nextWorkflowLabel || 'Transférer'}
        </button>
      )}
      <button
        onClick={onDelete}
        className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 text-white font-bold min-h-9 flex items-center gap-1.5"
        data-testid="batch-delete-btn"
      >
        <Trash2 size={13} /> Supprimer
      </button>
    </div>
    <button onClick={onClear} className="text-slate-400 hover:text-white flex items-center gap-1 text-[11px] py-2 px-2" data-testid="clear-selection-btn">
      <X size={12} /> Désélectionner
    </button>
  </div>
);

// ============================================================
// ORDERS TABLE
// ============================================================
const OrdersTable = ({ cols, rows, selectedIds, onToggleSelect, onToggleSelectAll, onRowClick, onOpenRow, driverNameById, onMoveColumn, onReorderColumn }) => {
  const [dragOverKey, setDragOverKey] = React.useState(null);
  return (
    <table className="w-full border-collapse text-left text-xs font-mono select-none">
      <thead className="bg-slate-900 border-b border-slate-800 text-slate-400 uppercase tracking-wider text-[10px] font-sans sticky top-0 z-20 shadow-sm">
        <tr>
          <th className="py-2 px-2.5 w-10 text-center bg-slate-900">
            <input
              type="checkbox"
              checked={selectedIds.size === rows.length && rows.length > 0}
              onChange={onToggleSelectAll}
              className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-950 w-4 h-4"
              data-testid="select-all-checkbox"
            />
          </th>
          <th className="w-10 bg-slate-900" />
          {cols.map((col, idx) => {
            const isFirst = idx === 0;
            const isLast = idx === cols.length - 1;
            const isDragOver = dragOverKey === col.key;
            return (
              <th
                key={col.key}
                className={`py-2 px-2.5 font-semibold text-slate-300 border-r border-slate-800/80 last:border-r-0 ${col.width} group/th relative ${isDragOver ? 'bg-amber-500/20 outline outline-2 outline-amber-500' : ''}`}
                draggable
                onDragStart={(e) => { e.dataTransfer.setData('text/col-key', col.key); e.dataTransfer.effectAllowed = 'move'; }}
                onDragOver={(e) => {
                  if (e.dataTransfer.types.includes('text/col-key')) {
                    e.preventDefault(); e.dataTransfer.dropEffect = 'move';
                    if (dragOverKey !== col.key) setDragOverKey(col.key);
                  }
                }}
                onDragLeave={() => setDragOverKey((k) => (k === col.key ? null : k))}
                onDrop={(e) => {
                  e.preventDefault();
                  const srcKey = e.dataTransfer.getData('text/col-key');
                  if (srcKey && srcKey !== col.key && onReorderColumn) onReorderColumn(srcKey, col.key);
                  setDragOverKey(null);
                }}
                data-testid={`col-header-${col.key}`}
              >
                <div className="flex items-center gap-1 cursor-grab active:cursor-grabbing">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onMoveColumn(col.key, -1); }}
                    disabled={isFirst}
                    className={`shrink-0 p-0.5 rounded ${isFirst ? 'opacity-20 cursor-not-allowed' : 'text-slate-500 hover:text-amber-400 hover:bg-slate-800'} md:opacity-0 md:group-hover/th:opacity-100 transition-opacity`}
                    draggable={false}
                  >
                    <ChevronLeft size={11} />
                  </button>
                  <span className="flex-1 truncate">{col.label}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onMoveColumn(col.key, 1); }}
                    disabled={isLast}
                    className={`shrink-0 p-0.5 rounded ${isLast ? 'opacity-20 cursor-not-allowed' : 'text-slate-500 hover:text-amber-400 hover:bg-slate-800'} md:opacity-0 md:group-hover/th:opacity-100 transition-opacity`}
                    draggable={false}
                  >
                    <ChevronRight size={11} />
                  </button>
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-800/60 text-slate-300">
        {rows.map((row, idx) => {
          const isSelected = selectedIds.has(row.id);
          const status = STATUS_META[row.status] || { label: row.status || '—', class: 'bg-slate-700/40 text-slate-400 border-slate-600/40' };
          const driverLabel = row.driverIds.length === 0 ? null
            : row.driverIds.length === 1 ? (driverNameById[row.driverIds[0]] || '—')
            : `${row.driverIds.length} chauffeurs`;
          return (
            <tr
              key={row.id}
              className={`hover:bg-slate-800/70 group cursor-pointer ${
                row.hasReturned
                  ? 'bg-red-500/20 hover:bg-red-500/30 border-l-4 border-red-500'
                  : isSelected
                    ? 'bg-amber-500/10'
                    : idx % 2 === 0 ? 'bg-slate-950' : 'bg-slate-900/30'
              }`}
              data-testid={`order-row-${row.order_number}`}
            >
              <td className="py-2 px-2.5 text-center">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => { e.stopPropagation(); onToggleSelect(row.id); }}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-slate-700 text-amber-500 focus:ring-amber-500 bg-slate-900 w-4 h-4"
                  data-testid={`select-checkbox-${row.order_number}`}
                />
              </td>
              <td className="py-2 px-1 text-center relative">
                <button
                  onClick={(e) => onRowClick(row, e)}
                  className="p-2 text-slate-400 hover:text-amber-400 rounded hover:bg-slate-700/60 min-h-11 min-w-11 md:opacity-70 md:group-hover:opacity-100 transition-opacity"
                  data-testid={`row-menu-${row.order_number}`}
                  aria-label="Actions"
                >
                  <MoreVertical size={16} />
                </button>
              </td>
              {cols.map((col) => {
                let content = row[col.key];
                if (col.key === 'status') content = <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-sans border ${status.class}`}>{status.label}</span>;
                else if (col.key === 'driver') content = driverLabel ? <span className="flex items-center gap-1.5"><User size={12} className="text-blue-400 shrink-0" /><span className="truncate">{driverLabel}</span></span> : <span className="text-amber-400/80 italic font-medium">Non assigné</span>;
                else if (col.key === 'logistique') content = row.assigned_logistique_name ? <span className="flex items-center gap-1.5 text-blue-300"><User size={12} className="text-blue-400 shrink-0" /><span className="truncate">{row.assigned_logistique_name}</span></span> : <span className="text-slate-500 italic">—</span>;
                else if (col.key === 'dispatcher') content = row.assigned_dispatcher_name ? <span className="flex items-center gap-1.5 text-emerald-300"><User size={12} className="text-emerald-400 shrink-0" /><span className="truncate">{row.assigned_dispatcher_name}</span></span> : <span className="text-slate-500 italic">—</span>;
                else if (col.key === 'order_number') content = (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onOpenRow(row); }}
                    className="font-bold text-amber-400 hover:text-amber-300 hover:underline text-left"
                    data-testid={`open-order-${row.order_number}`}
                  >
                    {content}
                  </button>
                );
                else if (col.key === 'client') content = <span className="font-sans font-medium text-white truncate block max-w-[170px]">{content || '—'}</span>;
                else if (col.key === 'pickup_time' || col.key === 'delivery_time') content = <span className="text-slate-300 font-semibold">{content || '—'}</span>;
                else content = content || '—';
                return (
                  <td key={col.key} className={`py-2 px-2.5 ${col.width}`}>{content}</td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ============================================================
// CONTEXT MENU
// ============================================================
const ContextMenu = ({ menu, assignmentType, allowShortcuts, onClose, onEdit, onAssign, onShortcutDispatcher, onDelete, onClearReturns, hasReturned }) => (
  <>
    <div className="fixed inset-0 z-40" onClick={onClose} />
    <div
      className="fixed z-50 w-60 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl py-1 text-xs"
      style={{ top: menu.y, left: menu.x }}
      onClick={(e) => e.stopPropagation()}
      data-testid="row-context-menu"
    >
      {hasReturned && (
        <>
          <button
            onClick={onClearReturns}
            className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-emerald-500/20 hover:text-emerald-300 text-emerald-400 min-h-11 font-semibold"
            data-testid="menu-clear-returns"
          >
            <ArrowRightCircle size={13} /> Marquer traité (retour)
          </button>
          <div className="my-1 border-t border-slate-800" />
        </>
      )}
      <button onClick={onEdit} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-slate-800 text-slate-200 min-h-11" data-testid="menu-edit">
        <Edit size={13} /> Voir / Modifier
      </button>
      {assignmentType && (
        <button onClick={onAssign} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-blue-500/20 hover:text-blue-300 text-slate-200 min-h-11" data-testid="menu-assign">
          <UserPlus size={13} /> {assignmentType === 'logistique' ? 'Attribuer à un agent…' : 'Attribuer au répartiteur…'}
        </button>
      )}
      {allowShortcuts && assignmentType !== 'dispatcher' && (
        <button onClick={onShortcutDispatcher} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-emerald-500/20 hover:text-emerald-300 text-emerald-400 min-h-11" data-testid="menu-shortcut-dispatcher">
          <ArrowRightCircle size={13} /> Raccourci → répartiteur…
        </button>
      )}
      <div className="border-t border-slate-800 my-1" />
      <button onClick={onDelete} className="w-full px-3 py-2.5 flex items-center gap-2 hover:bg-red-500/20 hover:text-red-300 text-red-400 min-h-11" data-testid="menu-delete">
        <Trash2 size={13} /> Supprimer…
      </button>
    </div>
  </>
);

// ============================================================
// ASSIGN AGENT MODAL
// ============================================================
const AssignAgentModal = ({ orderIds, agents, assignmentType, isShortcut, onClose, onPick }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="assign-agent-modal">
    <div className="bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-xl w-full sm:max-w-md max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="p-4 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold text-base">
            {isShortcut ? 'Raccourci → attribuer au répartiteur' : (assignmentType === 'logistique' ? 'Attribuer à un agent logistique' : 'Attribuer au répartiteur')}
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">
            {orderIds.length} commande{orderIds.length > 1 ? 's' : ''} sélectionnée{orderIds.length > 1 ? 's' : ''}
            {isShortcut && <span className="text-emerald-400 ml-2">· saute l'étape logistique</span>}
          </p>
        </div>
        <button onClick={onClose} className="p-2 text-slate-400 hover:text-white min-h-11 min-w-11" data-testid="close-assign-agent-modal">
          <X size={20} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {agents.length === 0 && (
          <div className="text-slate-500 text-sm text-center py-8">
            Aucun {assignmentType === 'logistique' ? 'agent logistique' : 'répartiteur'} configuré.
          </div>
        )}
        {agents.map((a) => (
          <button
            key={a.id}
            onClick={() => onPick(a.id)}
            className="w-full bg-slate-800 hover:bg-blue-500/20 hover:border-blue-500 border border-slate-700 rounded-lg p-3 flex items-center gap-3 text-left transition-colors min-h-16"
            data-testid={`pick-agent-${a.id}`}
          >
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-blue-400 font-bold shrink-0">
              {(a.first_name?.[0] || '?').toUpperCase()}{(a.last_name?.[0] || '').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white text-sm truncate">
                {`${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email}
              </div>
              <div className="text-xs text-slate-400 truncate">
                {a.email} · {a.role?.replace('_', ' ')}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// DELETE CONFIRM MODAL
// ============================================================
const DeleteConfirmModal = ({ count, orderNumbers, deleting, onCancel, onConfirm }) => {
  const preview = orderNumbers.slice(0, 5);
  const remaining = orderNumbers.length - preview.length;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onCancel} data-testid="delete-confirm-modal">
      <div className="bg-slate-900 border border-red-500/40 rounded-xl w-full max-w-md overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-800 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-400 shrink-0">
            <AlertCircle size={22} />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-base">Supprimer définitivement ?</h3>
            <p className="text-xs text-slate-400 mt-1">
              Cette action est <strong className="text-red-300">irréversible</strong>. Les commandes et tous leurs arrêts seront effacés.
            </p>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs font-mono">
            <div className="text-slate-400 mb-1.5 font-sans text-[11px] uppercase tracking-wider">
              {count} commande{count > 1 ? 's' : ''} à supprimer :
            </div>
            <div className="flex flex-wrap gap-1.5">
              {preview.map((num) => (
                <span key={num} className="px-2 py-0.5 rounded bg-red-500/10 border border-red-500/30 text-red-300">{num}</span>
              ))}
              {remaining > 0 && <span className="px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400">+{remaining} de plus…</span>}
            </div>
          </div>
        </div>
        <div className="p-4 bg-slate-950/50 border-t border-slate-800 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 min-h-11 disabled:opacity-50"
            data-testid="delete-cancel-btn"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-4 py-2 rounded-lg text-xs font-bold bg-red-600 hover:bg-red-500 text-white border border-red-500 min-h-11 flex items-center gap-2 disabled:opacity-60"
            data-testid="delete-confirm-btn"
          >
            {deleting ? <><Loader2 size={13} className="animate-spin" /> Suppression…</> : <><Trash2 size={13} /> Oui, supprimer</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// STATS FOOTER
// ============================================================
const StatsFooter = ({ stats, totalOrders }) => (
  <footer className="bg-slate-900 border-t border-slate-800 px-4 py-2 flex flex-wrap items-center justify-between text-xs text-slate-400 font-sans shrink-0">
    <div className="flex items-center gap-4">
      <span>Affichage : <strong className="text-white font-mono">{stats.count}</strong> sur {totalOrders} commandes</span>
      <span>Palettes : <strong className="text-white font-mono">{stats.totalPallets}</strong></span>
    </div>
    <div className="flex items-center gap-3 text-slate-500">
      <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-400"></span> Connecté</span>
    </div>
  </footer>
);

export default SacGridView;
